import {
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  Input
} from '@angular/core';
import { NgbActiveModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'suzy-action-forms-video-recorder',
  templateUrl: './video-recorder.component.html',
  styleUrls: ['./video-recorder.component.scss']
})
export class VideoRecorderComponent implements AfterViewInit, OnDestroy {
  @ViewChild('cameraVideo', { static: true })
  cameraVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('questionPopover', { static: false })
  questionPopoverRef!: NgbPopover;
  @Input() question: string;
  isRecording = false;
  recordingTime = 0;
  timerInterval: ReturnType<typeof setInterval> | null = null;
  canComplete = false;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedBlobs: Blob[] = [];
  private blob: Blob | null = null;
  private stream: MediaStream | null = null;

  readonly radius = 24;
  readonly circumference = 2 * Math.PI * this.radius;
  readonly MAX_TIME = 120; // seconds
  readonly PROGRESS_WARNING_TIME = 104; // 104 seconds = 86.7% of MAX_TIME; warning state for progress bar begins here
  readonly MIN_TIME = 15; // seconds
  svgRingCenter = 28.5; // center of svg circle
  @Input() isPreview: boolean = true;

  constructor(public activeModal: NgbActiveModal) {}

  async ngAfterViewInit() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      this.cameraVideoRef.nativeElement.srcObject = this.stream;
      // Mute the video element to prevent echo during recording
      this.cameraVideoRef.nativeElement.muted = true;

      // Open the question popover by default
      if (this.questionPopoverRef) {
        this.questionPopoverRef.open();
      }
    } catch (err) {
      this.activeModal.dismiss('Camera/Microphone access denied');
    }
  }

  closeModal() {
    this.cleanup();
    this.activeModal.dismiss();
  }

  toggleQuestionPopover() {
    if (this.questionPopoverRef) {
      this.questionPopoverRef.isOpen()
        ? this.questionPopoverRef.close()
        : this.questionPopoverRef.open();
    }
  }

  toggleRecording() {
    if (this.isPreview) {
      this.emitAndClose();
      return;
    }
    if (!this.isRecording) {
      // Start recording
      this.startRecording();
    } else {
      // Only allow stop if >= 15 seconds
      if (this.recordingTime >= this.MIN_TIME) {
        this.stopRecording();
      }
      // else do nothing
    }
  }

  startRecording() {
    if (!this.stream) return;
    this.recordedBlobs = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: 'video/webm'
      });
    } catch (e) {
      this.activeModal.dismiss('MediaRecorder not supported or mimeType error');
      return;
    }

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        this.recordedBlobs.push(event.data);
      }
    };

    this.mediaRecorder.onerror = event => {
      this.activeModal.dismiss('Recording error');
      this.cleanup();
    };

    this.mediaRecorder.onstop = () => {
      if (this.recordedBlobs.length) {
        this.blob = new Blob(this.recordedBlobs, { type: 'video/webm' });
        if (this.recordingTime >= this.MIN_TIME) {
          this.emitAndClose();
        }
      }
    };

    this.mediaRecorder.start();
    this.isRecording = true;
    this.recordingTime = 0;
    this.canComplete = false;
    this.timerInterval = setInterval(() => {
      this.recordingTime++;
      if (this.recordingTime >= this.MIN_TIME) {
        this.canComplete = true;
      }
      if (this.recordingTime >= this.MAX_TIME) {
        this.stopRecording();
      }
    }, 1000);
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }
  }

  private emitAndClose() {
    this.activeModal.close(this.blob);
    this.cleanup();
  }

  resetRecording() {
    this.stopRecording();
    this.recordedBlobs = [];
    this.recordingTime = 0;
    this.canComplete = false;
    this.isRecording = false;
    this.blob = null;
    // Optionally: restart camera preview if needed
  }

  get recordBtnBorderColor() {
    if (this.canComplete) {
      return '#3589F9';
    }
    if (this.isRecording) {
      return 'transparent';
    }
    return '#fff';
  }

  formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  }

  get dashOffset(): number {
    const progress = Math.min(this.recordingTime / this.MAX_TIME, 1);
    return this.circumference * (1 - progress);
  }

  get progressColor(): string {
    // White: Not enough recording time yet
    if (this.recordingTime <= this.MIN_TIME) return '#ffffff';
    // Blue: Safe recording window
    if (this.recordingTime <= this.PROGRESS_WARNING_TIME) return '#007aff';
    // Red: Approaching max recording time (warning)
    return '#ff3b30';
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.mediaRecorder = null;
    this.recordedBlobs = [];
    this.blob = null;
  }
}
